import axios from '@/assets/js/axios'
import Urls from '@/assets/js/Urls'

/**
 * 优惠券类型（与后端 CouponType 一致）
 */
export const CouponType = Object.freeze({
  /** 平台通用券 */
  Universal: 1,
  /** 指定产品类型 */
  SpecificGoodsType: 2,
  /** 指定商品 */
  SpecificGoodsId: 3
})

/**
 * 品类券（{@link CouponType.SpecificGoodsType}）时，scopeValue 表示产品类型
 */
export const CouponScopeGoodsType = Object.freeze({
  /** 锦囊 */
  JinNang: 1,
  /** 课程 */
  Course: 2,
  /** 点金 */
  DianJin: 4
})

/**
 * 用户券状态（与后端 UserCouponStatus 一致）
 */
export const UserCouponStatus = Object.freeze({
  /** 未使用 / 生效 */
  Valid: 0,
  /** 已使用 */
  Used: 1,
  /** 已锁定 */
  Locked: 2,
  /** 已过期 */
  Expired: 3,
  /** 已回收 */
  Recycled: 4
})

/**
 * @typedef {Object} CouponPageRequest
 * @property {number} direction 分页方向：0 首页/刷新，2 下一页（与点金一致）
 * @property {number} [userId]
 * @property {number} [type] CouponType，不传表示不按类型筛
 * @property {number} kind 固定 0
 * @property {null} productType 固定 null
 * @property {number[]} [statusList]
 */

/**
 * @typedef {Object} CouponUsageApiModel
 * @property {*} [key]
 */

/**
 * @typedef {Object} UserCouponApiModel
 * @property {string} couponNo
 * @property {number} couponId
 * @property {number} userId
 * @property {number} status UserCouponStatus（0 Valid，1 Used，2 Locked，3 Expired，4 Recycled）
 * @property {number} type CouponType
 * @property {number} kind
 * @property {number} scopeValue 品类券（type=2）时为产品类型：1 锦囊 2 课程 3 点金；其它类型见后端说明
 * @property {string} name
 * @property {string} description
 * @property {number} minLimitAmount
 * @property {number|null} valueAmount
 * @property {number|null} discount
 * @property {string} [appLinkUrl]
 * @property {number} validFrom ms
 * @property {number} validEnd ms
 * @property {number} createTime ms
 * @property {number} updateTime ms
 * @property {CouponUsageApiModel} [usage]
 */

/**
 * 订单优惠券请求（与后端 OrderCouponRequest 一致，JSON 多为 camelCase）
 * @typedef {Object} OrderCouponRequest
 * @property {number} goodsSetId 商品套餐 Id（对应 GoodsSetId）
 */

/**
 * 优惠方案（与后端 CouponOfferApiModel 一致）
 * @typedef {Object} CouponOfferApiModel
 * @property {string} name 优惠券名称
 * @property {string} couponNo 优惠券序列号
 * @property {number} discountAmount 优惠金额
 */

/**
 * 下单优惠券聚合（与后端 OrderCoupon 一致；成功时通常在响应 detail 中）
 * @typedef {Object} OrderCoupon
 * @property {UserCouponApiModel[]} coupons 用户所有有效优惠券
 * @property {CouponOfferApiModel[]} offers 本单可用的优惠方案
 */

/**
 * 查询下单时的优惠券与优惠方案
 * @param {number} goodsSetId 商品套餐 Id
 * @returns {Promise<*>} 整包响应（含 result.code、detail 为 {@link OrderCoupon}）；失败返回 null
 */
export function getOrderCoupon(goodsSetId) {
  return axios({
    url: `${Urls.DOMAIN_YKT}/Coupon/Api/OrderCoupon`,
    method: 'post',
    data: { goodsSetId }
  })
    .then(result => result)
    .catch(() => null)
}

/**
 * 用户优惠券分页（外层结构与点金 List 一致：result.viewState、detail.list、detail.end、detail.flush）
 * @param {CouponPageRequest} partialBody 除 kind、productType、direction 外的筛选字段；direction 可省略，由本函数合并
 * @param {string} viewState
 * @returns {Promise<*>}
 */
export function getCouponPage(partialBody, viewState) {
  const data = {
    kind: 0,
    productType: null,
    direction: viewState ? 2 : 0,
    ...partialBody
  }
  data.kind = 0
  data.productType = null
  data.direction = viewState ? 2 : 0

  return axios({
    url: `${Urls.DOMAIN_YKT}/Coupon/Api/Page`,
    method: 'post',
    data,
    headers: {
      'Ykt-ViewState': viewState
    }
  })
    .then(result => result)
    .catch(() => null)
}
